import React, { useState, useEffect } from 'react';

import { Fab, Tooltip, Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  DeleteForever
} from "@mui/icons-material";

import { AttachedTable, Condition, SourceBook } from "@dndModels";
import { 
  DisablableOption,
  FeatureBase, 
  OptionalFeatureBase,
  Feature,
} from "@models";
import { 
  LEVELED_SPELL_LEVELS_STRINGS,
  SOURCE_BOOK_TYPES 
} from "@dndModels/Constants";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

import FeatureListInput from "@dndComponents/feature/FeatureListInput";
import FeatureInput from "@dndComponents/feature/FeatureInput";
import SelectConditionBox from "@dndComponents/select/SelectConditionBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import DescriptionBox from "@dndComponents/DescriptionBox";
import { SourceBookType } from "@dndModels/Types";

interface Props {
  parent_name: string;
  optional_feature_base: OptionalFeatureBase;
  feature_bases: (FeatureBase | OptionalFeatureBase)[];
  disabled?: boolean;
  use_hybrid_feature_types?: string;
  // onChange: (feature_base: OptionalFeatureBase) => void; 
  onDelete: () => void; 
  onDone: () => void;
  attached_table?: AttachedTable;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function OptionalFeatureBaseInput({ 
  parent_name,
  optional_feature_base,
  feature_bases,
  disabled = false,
  use_hybrid_feature_types = "", 
  // onChange,
  onDelete,
  onDone,
  attached_table,
}: Props) {
  const [myFB, setFB] = useState<OptionalFeatureBase>(optional_feature_base.clone());
  const [expanded, setExpanded] = useState<Feature | null>(null);
  const [processing, setProcessing] = useState(false);
  const [unsaved_changes, setUnsavedChanges] = useState(false);
  const [source_types, setSourceTypes] = useState(optional_feature_base.source_book_types);
  const [reloading, setReloading] = useState(false);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const submit = () => {
    setProcessing(true);
    optional_feature_base.copy(myFB);
    api.upsertObject("optional_feature_base", optional_feature_base).then(res => {
      setProcessing(false);
      setUnsavedChanges(false);
    });
  }

  const render_replacement_key = () => {
    const return_us: any[] = [];
    return_us.push(
      <Grid
        key="key"
        size={{
          xs: 3
        }}>
        <StringBox
          name="Replacement Key"
          value={ myFB.replacement_key }
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = myFB;
            new_obj.replacement_key = changed;
            setFB(new_obj);
            setUnsavedChanges(true);
          }}
        />
      </Grid>
    );
    return_us.push(
      <Grid
        key="replace"
        size={{
          xs: 3
        }}>
        <ToggleButtonBox
          name="Replace Key"
          value={ myFB.replace_key }
          disabled={disabled}
          onToggle={() => {
            const new_obj = myFB;
            new_obj.replace_key = !new_obj.replace_key;
            setFB(new_obj);
            setUnsavedChanges(true);
          }}
        />
      </Grid>
    );
    return return_us;
  }

  const render_other_feature_base_select = (upgrade = false) => {
    const options: DisablableOption[] = [];
    for (const fb of feature_bases) {
      options.push({ 
        id: fb.id, 
        text: `${fb.fb_name} (${fb.level})`, 
        disabled: false,
        reason: ""
      });
    }
    let value = myFB.replaces_feature_base_id;
    if (upgrade) {
      value = myFB.upgrades_feature_base_id;
    }
    return (
      <SelectStringBox
        name={ upgrade ? "Upgrade Other Feature" : "Replace Other Feature" }
        options={options}
        extra_options={["None"]}
        value={ value }
        disabled={disabled}
        onChange={(changed: string) => {
          if (changed === "None") {
            changed = "";
          }
          const new_obj = myFB;
          if (upgrade) {
            new_obj.upgrades_feature_base_id = changed;
          } else {
            new_obj.replaces_feature_base_id = changed;
          }
          setFB(new_obj);
          setUnsavedChanges(true);
        }}
      />
    );
  }

  const renderConditionStuff = () => {
    const conditions = api.conditions;
    const required_condition = myFB.required_condition_ids.length === 1 ? conditions.find(o => o._id === myFB.required_condition_ids[0]) : undefined;
    
    return (
      <>
        <Grid
          size={{
            xs: required_condition && required_condition.max_level > 1 ? 3 : (myFB.required_condition_ids.length > 0 ? 5 : 8)
          }}>
          <SelectConditionBox
            name="Required Conditions"
            multiple
            resistances
            vulnerabilities
            immunities
            values={myFB.required_condition_ids}
            disabled={disabled}
            extra_options={["All"]}
            onChange={(ids: string[]) => {
              const new_obj = myFB;
              new_obj.required_condition_ids = ids;
              setFB(new_obj);
              setUnsavedChanges(true);
            }}
          />
        </Grid>
        { myFB.required_condition_ids.length > 0 &&
          <Grid
            size={{
              xs: 3
            }}>
            <CheckBox 
              value={myFB.exclude_conditions} 
              name="Exclude"
              disabled={disabled}
              onChange={(changed: boolean) => {
                const new_fb = myFB;
                new_fb.exclude_conditions = changed;
                setFB(new_fb);
                setUnsavedChanges(true);
              }}
            />
          </Grid>
        }
        { required_condition && required_condition.max_level > 1 && 
          renderRequiredConditionLevelSelect(required_condition) 
        }
      </>
    );
  }

  const renderRequiredConditionLevelSelect = (required_condition: Condition) => {
    const options: string[] = [];
    for (let i = 0; i < required_condition.max_level; ++i) {
      options.push(`${i}`);
    }
    return (
      <Grid
        size={{
          xs: 2
        }}>
        <SelectStringBox  
          name="Condition Level"
          options={options}
          value={`${myFB.condition_level}`}
          disabled={disabled}
          onChange={(changed: string) => {
            const new_fb = myFB;
            new_fb.condition_level = +changed;
            setFB(new_fb);
            setUnsavedChanges(true);
          }}
        />
      </Grid>
    );
  }

  if (expanded) {
    return (
      <FeatureInput 
        label="Feature"
        parent_name={parent_name}
        base_name={myFB.name}
        feature={expanded} 
        disabled={disabled}
        attached_table={attached_table}
        onChange={(changed: Feature) => {
          const new_obj = myFB;
          new_obj.features = common_util.replaceInList(new_obj.features, changed.true_id, changed, "true_id");
          setExpanded(changed);
          setFB(new_obj);
          setUnsavedChanges(true);
        }}
        onDelete={() => {
          const id = expanded.true_id;
          const new_obj = myFB;
          const features = new_obj.features.filter(o => o.true_id !== id);
          new_obj.features = features;
          setExpanded(null);
          setFB(new_obj);
          setUnsavedChanges(true);
        }}
        onDone={(target: string) => {
          if (target === "base") {
            setExpanded(null);
          } else {
            setExpanded(null);
            onDone();
          }
        }}
      />
    );
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <Button
            variant="contained"
            color="primary"
            disabled={ myFB.name === "" }
            onClick={ () => { 
              onDone();
            }}>
            { parent_name === "" ? `Nameless` : parent_name }
          </Button>
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { !disabled &&
            <>
              <Grid
                size={{
                  xs: 2
                }}>
                <Tooltip title={`Delete Optional Feature Base`}>
                  <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                    onClick={ () => {
                      if (optional_feature_base._id === "") {
                        onDelete();
                      } else {
                        setProcessing(true);
                        api.deleteObject("optional_feature_base", optional_feature_base).then(res => {
                          onDelete();
                        });
                      }
                    }}>
                    <DeleteForever/>
                  </Fab>
                </Tooltip>
              </Grid>
              <Grid
                style={{ color: "red" }}
                size={{
                  xs: 6
                }}>
                { optional_feature_base._id === "" ? 
                  "New Optional Feature Base, Unsaved" 
                : unsaved_changes && 
                  "Unsaved Changes"
                }
              </Grid>
            </>
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox 
            name="Name"
            value={myFB.name} 
            disabled={disabled}
            onBlur={(value: string) => {
              const new_obj = myFB;
              new_obj.name = value;
              setFB(new_obj);
              setUnsavedChanges(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <DescriptionBox 
            value={myFB.description} 
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = myFB;
              new_obj.description = changed;
              setFB(new_obj);
              setUnsavedChanges(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox 
            value={`${myFB.level}`} 
            name="Level"
            type="number"
            disabled={disabled}
            onBlur={(changed: number) => {
              const new_obj = myFB;
              new_obj.level = +changed;
              setFB(new_obj);
              setUnsavedChanges(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox
            name="Multiclassing"
            options={[
              { text: "Unaffected", id: "0" },
              { text: "First Class Only", id: "1" },
              { text: "Later Classes Only", id: "2" }
            ]}
            value={`${optional_feature_base.multiclassing}`}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = optional_feature_base;
              new_obj.multiclassing = +changed;
              setFB(new_obj);
              setUnsavedChanges(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox
            name="Required Spell Slot Level"
            options={LEVELED_SPELL_LEVELS_STRINGS}
            extra_options={["0"]}
            value={`${optional_feature_base.min_spell_slot_level}`}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = optional_feature_base;
              new_obj.min_spell_slot_level = +changed;
              setFB(new_obj);
              setUnsavedChanges(true);
            }}
          />
        </Grid>
        { renderConditionStuff() }
        <Grid
          size={{
            xs: 4
          }}>
          <CheckBox 
            name="Display"
            value={myFB.display} 
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = myFB;
              new_obj.display = changed;
              setFB(new_obj);
              setUnsavedChanges(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          Simple Replace 
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          Advanced Replace 
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          { render_other_feature_base_select(true) }
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          { render_other_feature_base_select() }
        </Grid>
        { render_replacement_key() }
        { api.user && api.user.admin ?
          <>
            <Grid
              size={{
                xs: 6
              }}>
              <SelectStringBox
                name="Source Types"
                disabled={disabled}
                values={source_types} 
                options={SOURCE_BOOK_TYPES}
                multiple
                onChange={(changed: SourceBookType[]) => {
                  setSourceTypes(changed);
                  setReloading(true);
                }}
              />
            </Grid>
            { !reloading &&
              <Grid
                size={{
                  xs: 6
                }}>
                <SelectObjectBox
                  name="Source Book"
                  values={myFB.source_book_ids} 
                  data_type="source_book"
                  filter={{ source_book_type: { "in": source_types }}}
                  disabled={disabled}
                  multiple
                  onChange={(ids: string[]) => {
                    const new_obj = myFB;
                    const source_books = (api.getDataTypeFromHash<SourceBook>("source_book")).filter(o => ids.includes(o._id));
                    if (new_obj) {
                      new_obj.source_books_old = source_books.map(o => { return { type: o.source_book_type, id: o._id, third_party: o.third_party }; });
                      setFB(new_obj);
                      setUnsavedChanges(true);
                    }
                  }}
                />
              </Grid>
            }
          </>
        : api.user &&
          <Grid
            size={{
              xs: 12
            }}>
            <SelectObjectBox
              name="Source Book"
              value={ myFB.source_book_ids[0] } 
              option_objects={api.owned_source_books}
              disabled={disabled}
              onChange={(id: string) => {
                const new_obj = myFB;
                const source_books = api.owned_source_books.filter(o => id === o._id);
                if (source_books.length > 0) {
                  new_obj.source_books_old = source_books.map(o => { return { type: o.source_book_type, id: o._id, third_party: o.third_party }; });
                }
                setFB(new_obj);
                setUnsavedChanges(true);
              }}
            />
          </Grid>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <FeatureListInput 
            label="Feature"
            features={myFB.features} 
            parent_id={myFB.parent_id} 
            parent_type={"Source"}
            disabled={ disabled || myFB.name === "" }
            onExpand={(feature: Feature) => {
              if (myFB.name !== "") {
                setExpanded(feature);
              }
            }}
            onAdd={() => {
              if (myFB.name !== "" && api.user) {
                const new_obj = myFB;
                const feature = new Feature();
                // feature.parent_type = new_obj.feature_source_type;
                feature.parent_id = new_obj.feature_source_id;
                feature.base_id = new_obj.id;
                new_obj.features.push(feature);
                setFB(new_obj);
                setExpanded(feature);
                setUnsavedChanges(true);
              }
            }}
          />
        </Grid>
        { !disabled && 
          <Grid
            size={{
              xs: 12
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={ processing }
              onClick={submit}>
              Submit
            </Button>
            <Button
              variant="contained"
              disabled={ processing }
              style={{ marginLeft: "4px" }}
              onClick={ () => { 
                const new_obj = myFB;
                new_obj.copy(optional_feature_base);
                setFB(new_obj);
                setUnsavedChanges(false);
              }}>
              Cancel
            </Button>
          </Grid>
        }
      </Grid>
    );
  }
}
